import { useState, useEffect, type ReactNode } from 'react';
import { Search, ChevronLeft, ChevronRight, Eye, Pencil } from 'lucide-react';
import { adminApi, AdminTenant, AdminTenantDetail } from '@/api/admin';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
} from '@/components/ui/drawer';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import {
  adminDrawerBodyClass,
  adminDrawerFooterClass,
  adminDrawerFormPaddingClass,
  adminDrawerScrollAreaClass,
  adminDrawerHeaderClass,
  adminDrawerShell,
  adminDrawerTitleClass,
  btnGhostNeon,
  btnOutlineNeon,
  btnPrimaryNeon,
  fieldClass,
  labelMutedClass,
} from '@/lib/admin-ui';
import { usePlatformPermission } from '@/hooks/use-platform-permission';
import { useUIStore } from '@/store/useUIStore';

export function TenantManagementPage() {
  const isLight = useUIStore((s) => s.theme === 'light');
  const lightPrimaryBtn =
    'border border-cyan-400/45 bg-cyan-500 text-slate-950 hover:bg-cyan-400';
  const lightOutlineBtn =
    'border border-slate-300 bg-white text-slate-700 hover:border-cyan-400/45 hover:bg-cyan-50 hover:text-cyan-700';
  const lightFieldClass =
    'border-slate-300 bg-white text-slate-800 placeholder:text-slate-400 focus-visible:ring-cyan-500/35';
  const canManageTenant = usePlatformPermission('platform:tenant:manage');
  const [tenants, setTenants] = useState<AdminTenant[]>([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
  });
  const [searchTerm, setSearchTerm] = useState('');

  // 查看详情相关状态
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState<AdminTenantDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // 编辑相关状态
  const [editOpen, setEditOpen] = useState(false);
  const [editingTenant, setEditingTenant] = useState<AdminTenant | null>(null);
  const [editForm, setEditForm] = useState({ name: '', status: '' });
  const [editLoading, setEditLoading] = useState(false);

  useEffect(() => {
    loadTenants();
  }, [pagination.page]);

  async function loadTenants() {
    try {
      setLoading(true);
      const response = await adminApi.getAllTenants(pagination.page, pagination.limit);
      setTenants(response.data);
      setPagination(response.pagination);
    } catch (error) {
      console.error('加载租户失败:', error);
    } finally {
      setLoading(false);
    }
  }

  async function handleView(tenant: AdminTenant) {
    setDetailLoading(true);
    setSelectedTenant(null);
    setDetailOpen(true);
    try {
      const detail = await adminApi.getTenantDetail(tenant.id);
      setSelectedTenant(detail);
    } catch (error) {
      console.error('加载租户详情失败:', error);
      toast.error('加载详情失败');
    } finally {
      setDetailLoading(false);
    }
  }

  function handleEdit(tenant: AdminTenant) {
    setEditingTenant(tenant);
    setEditForm({
      name: tenant.name,
      status: tenant.status,
    });
    setEditOpen(true);
  }

  async function handleSaveEdit() {
    if (!editingTenant) return;
    
    setEditLoading(true);
    try {
      await adminApi.updateTenant(editingTenant.id, {
        name: editForm.name,
        status: editForm.status,
      });
      
      // 更新本地数据
      setTenants(tenants.map(t => 
        t.id === editingTenant.id 
          ? { ...t, name: editForm.name, status: editForm.status as any }
          : t
      ));
      
      setEditOpen(false);
      toast.success('租户信息已更新');
    } catch (error) {
      console.error('更新租户失败:', error);
      toast.error('更新失败，请重试');
    } finally {
      setEditLoading(false);
    }
  }

  function getStatusVariant(status: AdminTenant['status']) {
    const variants: Record<string, 'default' | 'secondary' | 'destructive'> = {
      TRIAL: 'default',
      ACTIVE: 'default',
      SUSPENDED: 'destructive',
    };
    return variants[status] || 'secondary';
  }

  function getStatusLabel(status: AdminTenant['status']) {
    const labels = {
      TRIAL: '试用中',
      ACTIVE: '活跃',
      SUSPENDED: '已停用',
    };
    return labels[status] || status;
  }

  function getUserStatusLabel(status: string) {
    const labels: Record<string, string> = {
      ACTIVE: '正常',
      INACTIVE: '未激活',
      SUSPENDED: '已停用',
    };
    return labels[status] || status;
  }

  function getSubscriptionStatusLabel(status: string) {
    const labels: Record<string, string> = {
      TRIAL: '试用中',
      ACTIVE: '生效中',
      GRACE: '宽限期',
      EXPIRED: '已过期',
      CANCELLED: '已取消',
    };
    return labels[status] || status;
  }

  /** 详情内状态标签：青/neutral/红，与后台霓虹一致 */
  function DetailStatusBadge({
    tone,
    children,
  }: {
    tone: 'active' | 'muted' | 'danger' | 'trial';
    children: ReactNode;
  }) {
    return (
      <Badge
        variant="outline"
        className={cn(
          'border-0 font-medium',
          tone === 'active' &&
            'bg-cyan-500/15 text-cyan-200 shadow-[0_0_14px_-4px_rgba(34,211,238,0.35)]',
          tone === 'trial' &&
            'bg-amber-500/12 text-amber-200 shadow-[0_0_12px_-4px_rgba(251,191,36,0.2)]',
          tone === 'muted' && 'bg-zinc-800/90 text-zinc-400',
          tone === 'danger' &&
            'bg-red-500/15 text-red-300 shadow-[0_0_12px_-4px_rgba(248,113,113,0.2)]',
        )}
      >
        {children}
      </Badge>
    );
  }

  function tenantStatusTone(
    status: AdminTenant['status'],
  ): 'active' | 'muted' | 'danger' | 'trial' {
    if (status === 'ACTIVE') return 'active';
    if (status === 'TRIAL') return 'trial';
    if (status === 'SUSPENDED') return 'danger';
    return 'muted';
  }

  return (
    <div className="space-y-6">
      {/* 页面标题 */}
      <div>
        <h1 className="text-2xl font-bold text-foreground">租户管理</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          管理系统租户和订阅信息
        </p>
      </div>

      {/* 搜索栏 */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <Input
                placeholder="搜索租户名称..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={cn('pl-10', fieldClass, isLight && lightFieldClass)}
              />
            </div>
            <Button type="button" className={cn(isLight ? lightPrimaryBtn : btnPrimaryNeon)}>
              搜索
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* 租户表格 */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm font-medium">租户列表</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>租户名称</TableHead>
                <TableHead>状态</TableHead>
                <TableHead>用户数</TableHead>
                <TableHead>订阅数</TableHead>
                <TableHead>创建时间</TableHead>
                <TableHead>操作</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} className="h-24 text-center">
                    加载中...
                  </TableCell>
                </TableRow>
              ) : tenants.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="h-24 text-center text-muted-foreground">
                    暂无租户数据
                  </TableCell>
                </TableRow>
              ) : (
                tenants.map((tenant) => (
                  <TableRow key={tenant.id}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <Avatar>
                          <AvatarFallback className="bg-primary text-primary-foreground">
                            {tenant.name.charAt(0)}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <div className="font-medium">{tenant.name}</div>
                          <div className="text-xs text-muted-foreground">
                            ID: {tenant.id.slice(0, 8)}
                          </div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant={getStatusVariant(tenant.status)}>
                        {getStatusLabel(tenant.status)}
                      </Badge>
                    </TableCell>
                    <TableCell>{tenant.userCount} 人</TableCell>
                    <TableCell>{tenant.subscriptionCount} 个</TableCell>
                    <TableCell className="text-muted-foreground">
                      {new Date(tenant.createdAt).toLocaleDateString('zh-CN')}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className={btnGhostNeon}
                          onClick={() => handleView(tenant)}
                        >
                          <Eye className="mr-1 h-4 w-4" />
                          查看
                        </Button>
                        {canManageTenant && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className={btnGhostNeon}
                            onClick={() => handleEdit(tenant)}
                          >
                            <Pencil className="mr-1 h-4 w-4" />
                            编辑
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>

        {/* 分页 */}
        {pagination.totalPages > 0 && (
          <div className="flex items-center justify-between px-6 py-4 border-t">
            <div className="text-sm text-muted-foreground">
              共 {pagination.total} 条记录，第 {pagination.page} / {pagination.totalPages} 页
            </div>
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                size="icon"
                className={cn(isLight ? lightOutlineBtn : btnOutlineNeon)}
                onClick={() =>
                  setPagination((prev) => ({ ...prev, page: prev.page - 1 }))
                }
                disabled={pagination.page === 1}
              >
                <ChevronLeft className="h-5 w-5" />
              </Button>
              <Button
                type="button"
                variant="outline"
                size="icon"
                className={cn(isLight ? lightOutlineBtn : btnOutlineNeon)}
                onClick={() =>
                  setPagination((prev) => ({ ...prev, page: prev.page + 1 }))
                }
                disabled={pagination.page >= pagination.totalPages}
              >
                <ChevronRight className="h-5 w-5" />
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* 租户详情：右侧 Drawer，仅内容区滚动 */}
      <Drawer
        open={detailOpen}
        onOpenChange={(open) => {
          setDetailOpen(open);
          if (!open) setSelectedTenant(null);
        }}
        direction="right"
        shouldScaleBackground={false}
      >
        <DrawerContent
          className={cn(
            adminDrawerShell('detail'),
            isLight &&
              'border-slate-300 bg-white text-slate-900 [&>button]:border-slate-300 [&>button]:bg-white [&>button]:text-slate-500 [&>button]:hover:border-cyan-400/45 [&>button]:hover:bg-cyan-50 [&>button]:hover:text-cyan-700',
          )}
          data-admin-shell
        >
          <DrawerHeader className={cn(adminDrawerHeaderClass, 'space-y-0', isLight && 'border-b border-slate-300 bg-white')}>
            <DrawerTitle className={adminDrawerTitleClass}>租户详情</DrawerTitle>
          </DrawerHeader>

          <div
            className={cn(
              adminDrawerBodyClass,
              isLight &&
                'bg-white [&_.bg-zinc-900\\/55]:!bg-slate-50 [&_.border-cyan-500\\/12]:!border-slate-300 [&_.border-zinc-800\\/80]:!border-slate-300 [&_.border-zinc-800]:!border-slate-300 [&_.border-zinc-700]:!border-slate-300 [&_.text-zinc-100]:!text-slate-900 [&_.text-zinc-200]:!text-slate-800 [&_.text-zinc-300]:!text-slate-700 [&_.text-zinc-400]:!text-slate-600 [&_.text-zinc-500]:!text-slate-500 [&_.hover\\:bg-white\\/\\[0\\.03\\]]:hover:!bg-slate-50',
            )}
          >
            {detailLoading ? (
              <div className="py-16 text-center text-sm text-zinc-500">加载中…</div>
            ) : selectedTenant ? (
              <div className="space-y-8">
                <div
                  className={cn(
                    'grid grid-cols-2 gap-4 rounded-xl border p-4 backdrop-blur-sm',
                    isLight ? 'border-slate-300 bg-white' : 'border-cyan-500/12 bg-zinc-900/55',
                  )}
                >
                  <div>
                    <div className={cn('text-xs font-medium', isLight ? 'text-slate-500' : 'text-zinc-500')}>
                      租户名称
                    </div>
                    <div className={cn('mt-1 font-medium', isLight ? 'text-slate-900' : 'text-zinc-100')}>
                      {selectedTenant.name}
                    </div>
                  </div>
                  <div>
                    <div className={cn('text-xs font-medium', isLight ? 'text-slate-500' : 'text-zinc-500')}>
                      状态
                    </div>
                    <div className="mt-1">
                      <DetailStatusBadge tone={tenantStatusTone(selectedTenant.status)}>
                        {getStatusLabel(selectedTenant.status)}
                      </DetailStatusBadge>
                    </div>
                  </div>
                  <div>
                    <div className={cn('text-xs font-medium', isLight ? 'text-slate-500' : 'text-zinc-500')}>
                      用户数
                    </div>
                    <div className={cn('mt-1 font-medium', isLight ? 'text-slate-700' : 'text-zinc-200')}>
                      {selectedTenant.userCount} 人
                    </div>
                  </div>
                  <div>
                    <div className={cn('text-xs font-medium', isLight ? 'text-slate-500' : 'text-zinc-500')}>
                      订阅数
                    </div>
                    <div className={cn('mt-1 font-medium', isLight ? 'text-slate-700' : 'text-zinc-200')}>
                      {selectedTenant.subscriptionCount} 个
                    </div>
                  </div>
                  <div className="col-span-2 sm:col-span-1">
                    <div className={cn('text-xs font-medium', isLight ? 'text-slate-500' : 'text-zinc-500')}>
                      创建时间
                    </div>
                    <div className={cn('mt-1 font-medium', isLight ? 'text-slate-700' : 'text-zinc-200')}>
                      {new Date(selectedTenant.createdAt).toLocaleString('zh-CN')}
                    </div>
                  </div>
                  <div className="col-span-2 sm:col-span-1">
                    <div className={cn('text-xs font-medium', isLight ? 'text-slate-500' : 'text-zinc-500')}>
                      更新时间
                    </div>
                    <div className={cn('mt-1 font-medium', isLight ? 'text-slate-700' : 'text-zinc-200')}>
                      {new Date(selectedTenant.updatedAt).toLocaleString('zh-CN')}
                    </div>
                  </div>
                </div>

                <section>
                  <h3 className="mb-3 font-['Syne',sans-serif] text-base font-semibold text-zinc-100">
                    用户列表 ({selectedTenant.users.length})
                  </h3>
                  {selectedTenant.users.length > 0 ? (
                    <div className={cn('overflow-x-auto rounded-lg border', isLight ? 'border-slate-300' : 'border-zinc-800/80')}>
                      <Table>
                        <TableHeader>
                          <TableRow className={cn(isLight ? 'border-slate-300 hover:bg-transparent' : 'border-zinc-800 hover:bg-transparent')}>
                            <TableHead className="text-zinc-400">姓名</TableHead>
                            <TableHead className="text-zinc-400">邮箱</TableHead>
                            <TableHead className="text-zinc-400">状态</TableHead>
                            <TableHead className="text-zinc-400">创建时间</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {selectedTenant.users.map((user) => (
                            <TableRow
                              key={user.id}
                              className={cn(
                                isLight ? 'border-slate-300 hover:bg-slate-50' : 'border-zinc-800/80 hover:bg-white/[0.03]',
                              )}
                            >
                              <TableCell className="text-zinc-200">{user.name}</TableCell>
                              <TableCell className="text-zinc-400">{user.email}</TableCell>
                              <TableCell>
                                <DetailStatusBadge
                                  tone={
                                    user.status === 'ACTIVE'
                                      ? 'active'
                                      : user.status === 'SUSPENDED'
                                        ? 'danger'
                                        : 'muted'
                                  }
                                >
                                  {getUserStatusLabel(user.status)}
                                </DetailStatusBadge>
                              </TableCell>
                              <TableCell className="text-zinc-500">
                                {new Date(user.createdAt).toLocaleDateString('zh-CN')}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  ) : (
                    <div className={cn('rounded-lg border border-dashed py-8 text-center text-sm', isLight ? 'border-slate-300 text-slate-500' : 'border-zinc-700 text-zinc-500')}>
                      暂无用户
                    </div>
                  )}
                </section>

                <section>
                  <h3 className="mb-3 font-['Syne',sans-serif] text-base font-semibold text-zinc-100">
                    订阅列表 ({selectedTenant.subscriptions.length})
                  </h3>
                  {selectedTenant.subscriptions.length > 0 ? (
                    <div className={cn('overflow-x-auto rounded-lg border', isLight ? 'border-slate-300' : 'border-zinc-800/80')}>
                      <Table>
                        <TableHeader>
                          <TableRow className={cn(isLight ? 'border-slate-300 hover:bg-transparent' : 'border-zinc-800 hover:bg-transparent')}>
                            <TableHead className="text-zinc-400">系统</TableHead>
                            <TableHead className="text-zinc-400">套餐</TableHead>
                            <TableHead className="text-zinc-400">状态</TableHead>
                            <TableHead className="text-zinc-400">有效期</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {selectedTenant.subscriptions.map((sub) => (
                            <TableRow
                              key={sub.id}
                              className={cn(
                                isLight ? 'border-slate-300 hover:bg-slate-50' : 'border-zinc-800/80 hover:bg-white/[0.03]',
                              )}
                            >
                              <TableCell className="text-zinc-200">{sub.systemName}</TableCell>
                              <TableCell className="text-zinc-300">{sub.planName}</TableCell>
                              <TableCell>
                                <DetailStatusBadge
                                  tone={
                                    sub.status === 'ACTIVE'
                                      ? 'active'
                                      : sub.status === 'TRIAL'
                                        ? 'trial'
                                        : sub.status === 'EXPIRED' || sub.status === 'CANCELLED'
                                          ? 'danger'
                                          : 'muted'
                                  }
                                >
                                  {getSubscriptionStatusLabel(sub.status)}
                                </DetailStatusBadge>
                              </TableCell>
                              <TableCell className="text-zinc-500">
                                {new Date(sub.startAt).toLocaleDateString('zh-CN')} ~{' '}
                                {new Date(sub.endAt).toLocaleDateString('zh-CN')}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  ) : (
                    <div className={cn('rounded-lg border border-dashed py-8 text-center text-sm', isLight ? 'border-slate-300 text-slate-500' : 'border-zinc-700 text-zinc-500')}>
                      暂无订阅
                    </div>
                  )}
                </section>
              </div>
            ) : (
              <div className="py-16 text-center text-sm text-zinc-500">暂无数据</div>
            )}
          </div>
        </DrawerContent>
      </Drawer>

      {/* 编辑租户：右侧 Drawer */}
      <Drawer
        open={editOpen}
        onOpenChange={(open) => {
          setEditOpen(open);
          if (!open) setEditingTenant(null);
        }}
        direction="right"
        shouldScaleBackground={false}
      >
        <DrawerContent
          className={cn(
            adminDrawerShell('narrow'),
            isLight &&
              'border-slate-300 bg-white text-slate-900 [&>button]:border-slate-300 [&>button]:bg-white [&>button]:text-slate-500 [&>button]:hover:border-cyan-400/45 [&>button]:hover:bg-cyan-50 [&>button]:hover:text-cyan-700',
          )}
          data-admin-shell
        >
          <DrawerHeader className={cn(adminDrawerHeaderClass, isLight && 'border-b border-slate-300 bg-white')}>
            <DrawerTitle className={adminDrawerTitleClass}>编辑租户</DrawerTitle>
            <DrawerDescription className={cn('text-sm', isLight ? 'text-slate-500' : 'text-zinc-500')}>
              修改租户名称与状态
            </DrawerDescription>
          </DrawerHeader>

          <div className={cn(adminDrawerScrollAreaClass, isLight && 'bg-white')}>
            <div className={cn(adminDrawerFormPaddingClass, 'space-y-4')}>
              <div className="grid gap-2">
                <Label htmlFor="tenant-name" className={labelMutedClass}>
                  租户名称
                </Label>
                <Input
                  id="tenant-name"
                  value={editForm.name}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                  placeholder="请输入租户名称"
                  className={cn(fieldClass, isLight && lightFieldClass)}
                />
              </div>

              <div className="grid gap-2">
                <Label htmlFor="tenant-status" className={labelMutedClass}>
                  状态
                </Label>
                <Select
                  value={editForm.status}
                  onValueChange={(value) => setEditForm({ ...editForm, status: value })}
                >
                  <SelectTrigger id="tenant-status" className={cn(fieldClass, isLight && lightFieldClass)}>
                    <SelectValue placeholder="选择状态" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="TRIAL">试用中</SelectItem>
                    <SelectItem value="ACTIVE">活跃</SelectItem>
                    <SelectItem value="SUSPENDED">已停用</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          <div className={cn(adminDrawerFooterClass, 'justify-end', isLight && 'border-slate-300 bg-white')}>
            <Button
              type="button"
              variant="outline"
              className={cn(isLight ? lightOutlineBtn : btnOutlineNeon)}
              onClick={() => setEditOpen(false)}
            >
              取消
            </Button>
            <Button
              type="button"
              className={cn(isLight ? lightPrimaryBtn : btnPrimaryNeon)}
              onClick={handleSaveEdit}
              disabled={editLoading}
            >
              {editLoading ? '保存中…' : '保存'}
            </Button>
          </div>
        </DrawerContent>
      </Drawer>
    </div>
  );
}
